import { Head, Link, router } from "@inertiajs/react"
import { ArrowLeft, Trash2 } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { AutomationForm } from "@/components/admin/AutomationForm"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import type { AutomationEditDetail, LanguageOptions, ScopeOption } from "@/types/automation"

export default function AdminAutomationsEdit({
  automation,
  scope_options,
  language_options,
}: {
  automation: AutomationEditDetail
  scope_options: ScopeOption[]
  /** Deferred: undefined until the roster has been asked. */
  language_options?: LanguageOptions
}) {
  const description = `Edit the ${automation.name} automation — its wording, its roster query, and its default send time.`

  return (
    <>
      <Head title={automation.name}>
        <meta name="description" content={description} />
        <meta property="og:title" content={automation.name} />
        <meta property="og:description" content={description} />
      </Head>
      <AdminShell>
        <Link
          href="/admin/automations"
          className="mb-4 inline-flex items-center gap-1.5 text-sm no-underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to automations
        </Link>

        <PageHeader
          title={automation.name}
          description={automation.description ?? undefined}
          actions={
            <Badge tone={automation.is_active ? "accent" : "muted"}>
              {automation.is_active ? "Active" : "Inactive"}
            </Badge>
          }
        />

        <div className="mt-6">
          <FlashNotice />
        </div>

        <AutomationForm
          automation={automation}
          scopeOptions={scope_options}
          languageOptions={language_options}
          submitLabel="Save changes"
        />

        <p className="mt-8 text-xs text-ink-muted">
          {automation.created_by
            ? `Created by ${automation.created_by}.`
            : "Shipped with the app."}{" "}
          {automation.query_checked_at
            ? `Query last checked against the roster ${formatDateTime(automation.query_checked_at)}.`
            : "This query has not been checked against the roster yet."}
        </p>

        <DeleteSection automation={automation} />
      </AdminShell>
    </>
  )
}

function plural(count: number, noun: string) {
  return `${count} ${count === 1 ? noun : `${noun}s`}`
}

/**
 * Delete, which archives anything that has run. The copy says which will happen
 * before the admin confirms; the server decides again under a lock, so a run
 * that opens in between turns the delete into an archive and the flash says so.
 */
function DeleteSection({ automation }: { automation: AutomationEditDetail }) {
  const willDelete = automation.run_count === 0

  const summary = willDelete
    ? `${automation.name} has never run, not even as a rehearsal, so deleting it removes it for good${
        automation.setup_count > 0
          ? `, along with ${plural(automation.setup_count, "organization setup")}`
          : ""
      }. There is no history to keep.`
    : `${automation.name} has run ${plural(automation.run_count, "time")}, so it can't be deleted: its history records who was texted what. Deleting archives it instead.`

  const remove = () => {
    router.delete(`/admin/automations/${automation.id}`, {
      data: { expected: willDelete ? "delete" : "archive" },
    })
  }

  return (
    <section className="mt-10 border-t border-hairline pt-6">
      <h2>Delete this automation</h2>
      <p className="mt-1">{summary}</p>

      <Dialog>
        <DialogTrigger asChild>
          <Button variant="danger" className="mt-4">
            <Trash2 className="h-4 w-4" />
            Delete…
          </Button>
        </DialogTrigger>
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>
              {willDelete ? `Delete ${automation.name}?` : `Archive ${automation.name}?`}
            </DialogTitle>
            <DialogDescription>
              {willDelete ? (
                <>
                  It has never run, so it is deleted for good
                  {automation.setup_count > 0
                    ? `, with every organization's setup of it (${automation.setup_count})`
                    : ""}
                  . This can't be undone.
                </>
              ) : (
                <>
                  It has run {plural(automation.run_count, "time")}, so it is
                  archived rather than deleted.{" "}
                  {automation.enabled_setup_count > 0
                    ? `${plural(automation.enabled_setup_count, "setup")} switched on will be switched off. `
                    : "No setup is switched on right now. "}
                  It leaves every organization's catalog and the scheduler
                  skips it. Its run history stays readable, and it can be
                  reactivated from Archived automations.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost">Keep it</Button>
            </DialogClose>
            <DialogClose asChild>
              <Button variant="danger" onClick={remove}>
                {willDelete ? "Delete for good" : "Archive automation"}
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  })
}
